import { useCallback } from 'react';
import { BLOCKCHAINS, PROVIDERS, getBlockchainByChainId, getEvmChainId, getWhChainId, isEvmChain } from './providers';
import { useAppDispatch, useAppSelector } from '../services/hooks.service';
import { resetActiveWallet, saveKYCStatus, saveMainWallet } from '../redux/_actions/user.action';
import { setNetworkConfig } from '../redux/_actions/network.action';
import { useActiveAccount } from './useActiveAccount'
import { PeraConnector } from './algorand/pera.service';
import { SolanaConnector } from './solana/solana.service';
import { EVMConnector } from './evm/evm.service';
import RtkSdkAdaptor from '@ultrade/react-sdk';
import { saveUserAssets } from '../redux/_actions/wallet.action';
import { errorHandler } from '../_utils/errorHandlers';
import { clearReferralToken, getReferralToken } from '../services/localStorage.service';//@ts-ignore
import { KYCAuthenticationStatus } from '@libs/shared/db/enums/account/index';
import { generateLoadingToast, getUpdatedToastOptions } from '../_utils';
import { toast } from 'react-toastify';
import { SolanaNetwork } from './solana/network';

const solanaConnector = SolanaConnector.getInstance();
const evmConnector = EVMConnector.getInstance();
const peraConnector = PeraConnector.getInstance();

export const useUltradeWallet = () => {
  const sdkClient = useAppSelector((state) => state.sdkClient.instance);
  const network = useAppSelector(state => state.network);
  const isKYCEnabled = useAppSelector((state) => state.settings.kycTradeRequirementEnabled);
  const { chain } = network;
  const dispatch = useAppDispatch();
  const activeAcc = useActiveAccount();

  const disconnect = useCallback(async () => {
    console.log('disconnect', {
      evmConnector: { ...evmConnector },
      peraConnector: { ...peraConnector },
      solanaConnector: { ...solanaConnector }
    })

    //evm
    if (evmConnector.isConnected && evmConnector.address) {
      await evmConnector.disconnect()
        .catch(() => {
          console.log('Not disconnected. Try again');
          evmConnector.disconnect();
        });
    }

    //pera
    if (peraConnector.isConnected) {
      await peraConnector.disconnect();
    }

    //solana
    if (solanaConnector.isConnected) {
      await solanaConnector.disconnect();
    }

    dispatch(setNetworkConfig({
      chain: null,
      provider: null,
      whChainId: null,
      evmChainId: null,
      isConnected: false,
    }));
    dispatch(resetActiveWallet());
  }, [dispatch]);

  const connect = useCallback(async (blockchain: BLOCKCHAINS, provider: PROVIDERS) => {
    await disconnect();
    let connectedChain: BLOCKCHAINS;
    try {
      if (isEvmChain(blockchain)) {
        connectedChain = await evmConnector.connect(blockchain, provider);
        console.log('Connected blockchain: ', connectedChain)
      }
      if (blockchain === BLOCKCHAINS.ALGORAND) {
        // TODO modify for defly
        connectedChain = await peraConnector.connect(blockchain);
      } 
      if (blockchain === BLOCKCHAINS.SOLANA) {
        connectedChain = await solanaConnector.connect(blockchain, provider);
        if (sdkClient.mainWallet && sdkClient.mainWallet.token && sdkClient.mainWallet.address) {
          SolanaNetwork.updateConnection({
            'X-Wallet-Token': sdkClient.mainWallet.token,
            'X-Wallet-Address': sdkClient.mainWallet.address,
          });
        }
      }
      console.log('connect', { blockchain, provider, connectedChain })
      dispatch(setNetworkConfig({
        chain: connectedChain,
        provider: provider,
        whChainId: getWhChainId(blockchain),
        evmChainId: getEvmChainId(blockchain),
        isConnected: true,
      }));
      return connectedChain;
    } catch (error) {
      errorHandler(error);
    }
  }, [disconnect, dispatch]);

  const signInAndLogin = async () => {
    const toastId = generateLoadingToast("Logging in...");
    try {
      if (!activeAcc || !sdkClient.companyId) {
        toast.dismiss(toastId);
        return;
      };
      const referralToken = getReferralToken();
      //TODO: investigate why we do 3 sign ins
      // console.log('Sign in with: ', activeAcc.address, activeAcc.provider, chain);
      //technology
      const newToken = await sdkClient.login({ address: activeAcc.address, provider: activeAcc.provider, chain, referralToken });
      if (newToken) {
        dispatch(saveMainWallet({...activeAcc, chain}));  
        if (sdkClient.mainWallet) {
          const existingTradingKey = RtkSdkAdaptor.originalSdk.mainWallet?.tradingKey;
          RtkSdkAdaptor.originalSdk.mainWallet = sdkClient.mainWallet;
          if (existingTradingKey) {
            RtkSdkAdaptor.originalSdk.mainWallet.tradingKey = existingTradingKey;
          }
        }
        // solana proxy rpc update
        if (sdkClient.mainWallet.chain === BLOCKCHAINS.SOLANA) {
          SolanaNetwork.updateConnection({
            'X-Wallet-Token': sdkClient.mainWallet.token,
            'X-Wallet-Address': sdkClient.mainWallet.address,
          });
        }

        if (isKYCEnabled) getKYCStatus();
        if (referralToken) clearReferralToken();
      }
      toast.update(toastId, getUpdatedToastOptions("success", "Successfully logged in"));
    } catch (error) {
      toast.update(toastId, getUpdatedToastOptions("error", "Failed to log in"));
      errorHandler(error);
    } finally {
      toast.dismiss(toastId);
    }
  };

  const getKYCStatus = useCallback(async (): Promise<null | KYCAuthenticationStatus> => {
    try {
      const kycStatus = await sdkClient.getKycStatus();
      if (kycStatus) {
        dispatch(saveKYCStatus(kycStatus.kycStatus))
        return kycStatus.kycStatus;
      }
    } catch (error) {
      errorHandler(error);
    }
  }, []);

  const switchEvmChain = useCallback(async (chain: BLOCKCHAINS) => {
    try {
      const newCHainId = await evmConnector.switchChain(chain);
      const blockchain = getBlockchainByChainId(newCHainId);
      dispatch(saveUserAssets([]));
      console.log('set new chain to state: ', blockchain);
    } catch (e) {
      console.log('error to set new chain: ', e);
    }
  }, []);

  return {
    connect,
    disconnect,
    signInAndLogin,
    getKYCStatus,
    switchEvmChain,
  };
};
